import React, { useState, useEffect } from "react";
import classesByDay from "./classesData";
import { FaClock, FaUserTie, FaWhatsapp, FaArrowRight } from "react-icons/fa";

const Classes = () => {
  const [selectedDay, setSelectedDay] = useState("");

  useEffect(() => {
    const daysOfWeek = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];
    const currentDate = new Date();
    const currentDayName = daysOfWeek[currentDate.getDay()];
    const availableDays = Object.keys(classesByDay);
    setSelectedDay(
      availableDays.includes(currentDayName) ? currentDayName : availableDays[0]
    );
  }, []);

  const renderClassCard = (classItem, index) => (
    <div
      key={index}
      className="group bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6 hover:bg-slate-800 hover:border-blue-500/30 transition-all duration-300"
    >
      <h3 className="font-bold text-white mb-4 text-lg group-hover:text-blue-300 transition-colors">
        {classItem.name}
      </h3>
      <div className="space-y-3 mb-5">
        <div className="flex items-center gap-3 text-sm">
          <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center">
            <FaClock className="text-blue-400 text-xs" />
          </div>
          <span className="text-slate-300">{classItem.time}</span>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center">
            <FaUserTie className="text-blue-400 text-xs" />
          </div>
          <span className="text-slate-300">{classItem.trainer}</span>
        </div>
      </div>
      <a
        href={`https://wa.me/919582691655?text=Hi%2C%20I%27d%20like%20to%20reserve%20a%20spot%20in%20${encodeURIComponent(classItem.name)}%20on%20${selectedDay}`}
        target="_blank"
        rel="noopener noreferrer"
        className="w-full inline-flex items-center justify-center gap-2 bg-white/5 border border-white/10 text-white hover:bg-primary-600 hover:border-primary-600 text-sm font-medium py-2.5 rounded-xl transition-all"
      >
        <FaWhatsapp />
        Reserve Spot
      </a>
    </div>
  );

  return (
    <section className="pt-24 pb-16 bg-slate-950 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-14">
          <p className="text-blue-400 font-semibold text-sm uppercase tracking-wider mb-3">
            Schedule
          </p>
          <h1 className="text-3xl md:text-5xl font-black text-white mb-4">
            Class Schedule
          </h1>
          <p className="text-slate-400 max-w-xl mx-auto">
            Find the right class for you or your child. We run classes 6 days a
            week with morning and evening batches.
          </p>
        </div>

        {/* Day selector */}
        <div className="flex flex-wrap justify-center gap-2 mb-12">
          {Object.keys(classesByDay).map((day) => (
            <button
              key={day}
              onClick={() => setSelectedDay(day)}
              className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 ${
                selectedDay === day
                  ? "bg-primary-600 text-white shadow-lg shadow-primary-600/25"
                  : "bg-white/5 text-slate-400 hover:bg-white/10 hover:text-white border border-white/10"
              }`}
            >
              {day}
            </button>
          ))}
        </div>

        {/* Classes */}
        {classesByDay[selectedDay] && (
          <div className="space-y-12">
            {/* Morning Batch */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-2 h-2 rounded-full bg-amber-400" />
                <h2 className="text-lg font-bold text-white">Morning Batch</h2>
                <div className="flex-1 h-px bg-slate-800" />
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {classesByDay[selectedDay]
                  .filter((c) => {
                    const hour = parseInt(c.time.split(":")[0]);
                    return hour < 12 || c.time.includes("AM");
                  })
                  .map(renderClassCard)}
              </div>
            </div>

            {/* Evening Batch */}
            {classesByDay[selectedDay].filter((c) => {
              const hour = parseInt(c.time.split(":")[0]);
              return (hour >= 12 && !c.time.includes("AM")) || c.time.includes("PM");
            }).length > 0 && (
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-2 h-2 rounded-full bg-blue-400" />
                  <h2 className="text-lg font-bold text-white">Evening Batch</h2>
                  <div className="flex-1 h-px bg-slate-800" />
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {classesByDay[selectedDay]
                    .filter((c) => {
                      const hour = parseInt(c.time.split(":")[0]);
                      return (hour >= 12 && !c.time.includes("AM")) || c.time.includes("PM");
                    })
                    .map(renderClassCard)}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Free Trial CTA */}
        <div className="mt-16 bg-slate-900 border border-slate-800 rounded-2xl p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="text-xl font-bold text-white mb-2">
              Want to try before joining?
            </h3>
            <p className="text-slate-400 text-sm">
              We offer 2 free trial classes. No commitment, no pressure.
            </p>
          </div>
          <a
            href="https://wa.me/919582691655?text=Hi%2C%20I%27d%20like%20to%20book%20a%20free%20trial%20class"
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-3 bg-white text-slate-900 font-bold px-7 py-4 rounded-full hover:bg-blue-50 transition-all flex-shrink-0"
          >
            <FaWhatsapp className="text-green-500" />
            Book Free Trial
            <FaArrowRight className="text-sm group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default Classes;